import { useRef } from "react";
import { Instagram, MapPin, Phone, Mail, Clock, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { sanitizeMapsEmbed } from "@/lib/mapsEmbed";

/**
 * OfficeSection — location, address, hours, and Google Maps embed.
 * All copy is editable from Admin Panel via /api/settings/office.
 */
export function OfficeSection({ office }) {
  if (!office) return null;
  const {
    heading = "Visit our studio",
    address_lines = [],
    phone = "",
    email = "",
    hours = "",
    maps_embed_url = "",
    maps_link = "",
  } = office;
  const embedSrc = sanitizeMapsEmbed(maps_embed_url);

  return (
    <section data-testid="office-section" className="max-w-7xl mx-auto px-6 lg:px-8 py-24 lg:py-32">
      <div className="grid lg:grid-cols-5 gap-8 lg:gap-12 items-stretch">
        {/* Left — address card */}
        <div className="lg:col-span-2 flex flex-col justify-center">
          <div className="eyebrow mb-3">Find us</div>
          <h2 className="font-serif text-4xl lg:text-5xl text-[#292724] font-light gold-underline mb-6">{heading}</h2>
          <p className="text-sm text-[#5a544c] leading-relaxed mb-6">
            Drop by our studio to touch fabrics, feel textures and meet the curators who bring your celebration to life.
          </p>

          {address_lines.length > 0 && (
            <div className="flex items-start gap-3 mb-4" data-testid="office-address">
              <div className="p-2 rounded-full bg-[#faf7f2] shrink-0 mt-0.5"><MapPin className="w-4 h-4 text-[#d4af37]" /></div>
              <div className="text-sm text-[#292724] leading-relaxed">
                {address_lines.map((line, i) => <div key={i}>{line}</div>)}
              </div>
            </div>
          )}
          {phone && (
            <div className="flex items-center gap-3 mb-4" data-testid="office-phone">
              <div className="p-2 rounded-full bg-[#faf7f2] shrink-0"><Phone className="w-4 h-4 text-[#d4af37]" /></div>
              <a href={`tel:${phone.replace(/\s/g, "")}`} className="text-sm text-[#292724] hover:text-[#d4af37]">{phone}</a>
            </div>
          )}
          {email && (
            <div className="flex items-center gap-3 mb-4" data-testid="office-email">
              <div className="p-2 rounded-full bg-[#faf7f2] shrink-0"><Mail className="w-4 h-4 text-[#d4af37]" /></div>
              <a href={`mailto:${email}`} className="text-sm text-[#292724] hover:text-[#d4af37]">{email}</a>
            </div>
          )}
          {hours && (
            <div className="flex items-center gap-3 mb-6" data-testid="office-hours">
              <div className="p-2 rounded-full bg-[#faf7f2] shrink-0"><Clock className="w-4 h-4 text-[#d4af37]" /></div>
              <span className="text-sm text-[#292724]">{hours}</span>
            </div>
          )}
          {maps_link && (
            <a href={maps_link} target="_blank" rel="noreferrer" data-testid="office-directions" className="btn-outline-gold w-fit text-sm">
              Get directions <ExternalLink className="w-4 h-4" />
            </a>
          )}
        </div>

        {/* Right — Google Maps embed (or fallback illustration) */}
        <div className="lg:col-span-3">
          <div className="rounded-3xl overflow-hidden border border-[#ebe6e0] shadow-[0_20px_60px_rgba(0,0,0,0.06)] bg-[#faf7f2]" data-testid="office-map">
            {embedSrc ? (
              <iframe
                data-testid="office-map-iframe"
                title="Studio location"
                src={embedSrc}
                width="100%"
                height="400"
                className="block w-full"
                style={{ border: 0 }}
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                allowFullScreen
              />
            ) : (
              <div className="w-full h-full min-h-[420px] flex flex-col items-center justify-center text-[#8a837a] text-sm p-6">
                <MapPin className="w-8 h-8 mb-3 text-[#d4af37]" />
                <div className="font-serif text-lg text-[#292724]">Map coming soon</div>
                <div className="text-xs mt-1">Admin can paste a Google Maps embed URL to activate this map.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

/**
 * InstagramSection — standalone section: heading + follow link + arrow slider of posts.
 * Data comes from /api/settings/instagram (admin-editable).
 */
export function InstagramSection({ insta, socialUrl = "", embedded = false }) {
  const posts = insta?.posts || [];
  const trackRef = useRef(null);
  if (!posts.length) return null;
  const handle = insta?.handle || "@tentntap";
  const followUrl = socialUrl || `https://instagram.com/${handle.replace(/^@/, "")}`;
  const Wrapper = embedded ? "div" : "section";
  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const card = el.querySelector("[data-ig-card]");
    const step = card ? card.getBoundingClientRect().width + 16 : el.clientWidth * 0.8;
    el.scrollBy({ left: dir * step, behavior: "smooth" });
  };

  return (
    <Wrapper data-testid="instagram-section" className={embedded ? "w-full min-w-0" : "max-w-7xl mx-auto px-6 lg:px-8 py-20 lg:py-28 border-t border-[#ebe6e0]"}>
      <div className={`${embedded ? "mb-3" : "mb-8 lg:mb-10"} flex flex-row items-center justify-between gap-3`}>
        <div className="min-w-0">
          <div className={`eyebrow ${embedded ? "mb-0" : "mb-3"} flex items-center gap-2`}><Instagram className="w-3.5 h-3.5 text-[#d4af37]" /> On Instagram</div>
          {!embedded && (
            <h2 className="font-serif text-4xl lg:text-5xl text-[#292724] font-light gold-underline">
              Follow the <em className="italic text-[#d4af37]">celebrations</em>.
            </h2>
          )}
        </div>
        <div className="flex items-center gap-3">
          <a
            href={followUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="instagram-follow-link"
            className={`inline-flex items-center gap-2 rounded-full bg-[#292724] text-white hover:bg-[#d4af37] hover:text-[#292724] transition-colors ${embedded ? "px-3.5 py-1.5 text-xs" : "px-5 py-2.5 text-sm"}`}
          >
            <Instagram className="w-4 h-4" /> {handle}
          </a>
          <div className="hidden sm:flex items-center gap-2">
            <button type="button" data-testid="instagram-prev" onClick={() => scrollBy(-1)} aria-label="Previous"
              className={`${embedded ? "w-8 h-8" : "w-10 h-10"} rounded-full border border-[#ebe6e0] bg-white hover:border-[#d4af37] hover:text-[#d4af37] flex items-center justify-center transition-colors`}>
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button type="button" data-testid="instagram-next" onClick={() => scrollBy(1)} aria-label="Next"
              className={`${embedded ? "w-8 h-8" : "w-10 h-10"} rounded-full border border-[#ebe6e0] bg-white hover:border-[#d4af37] hover:text-[#d4af37] flex items-center justify-center transition-colors`}>
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div
        ref={trackRef}
        data-testid="instagram-carousel"
        className={`flex ${embedded ? "gap-3" : "gap-4"} overflow-x-auto snap-x snap-mandatory pb-3 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden`}
      >
        {posts.map((p, i) => {
          const inner = (
            <div data-ig-card className={`${embedded ? "w-[140px] sm:w-[160px] md:w-[180px]" : "w-[min(70vw,260px)] sm:w-[220px] md:w-[260px]"} aspect-square overflow-hidden rounded-2xl bg-[#faf7f2] relative group border border-[#ebe6e0]`}>
              {p.image ? (
                <img loading="lazy" src={p.image} alt={p.caption || "Instagram"} className="w-full h-full object-cover object-center transition-transform duration-500 group-hover:scale-110" />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-[#8a837a] text-xs">No photo</div>
              )}
              <div className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 flex items-center justify-center shadow">
                <Instagram className="w-4 h-4 text-[#292724]" />
              </div>
            </div>
          );
          const cls = "snap-start shrink-0";
          return p.link ? (
            <a key={`${i}-${p.link}`} href={p.link} target="_blank" rel="noreferrer" data-testid={`insta-${i}`} className={cls}>{inner}</a>
          ) : (
            <div key={i} data-testid={`insta-${i}`} className={cls}>{inner}</div>
          );
        })}
      </div>
    </Wrapper>
  );
}
